import React, { useContext, useEffect, useState } from "react";

import { IPassportDetailsResponseData } from "@tm/api-utils";
import { getAnalytics } from "@tm/insurance/analytics";
import { trackEvent } from "@tm/insurance/analytics/xdm";
import { editInsuranceDetails } from "@tm/insurance/api";
import { navigationActions } from "@tm/insurance/navigation";
import { getPartnerOnClientSide } from "@tm/insurance/utils";
import {
  PASSPORT_DETAILS_PAGE_ID,
  RESPONSE_STATUS,
} from "@tm/isomorphic-utils";
import {
  AddPassportModal,
  DesktopLayout,
  InsuranceBooked,
  MoreBenefits,
  PassportNumComp,
} from "@tm/static";
import { usePlanType } from "@tm/ui";
import { ExperimentTrackingString } from "@tm/ui-widgets";
import { ClientError } from "@tm/utility";
import { ITravellerDetail } from "../../components/PassportNumComp/types";

type InsAddPassportProps = {
  passportDetailsData: IPassportDetailsResponseData;
};

const analytics = getAnalytics();

const InsuranceAddPassport = ({ passportDetailsData }: InsAddPassportProps) => {
  const [pageId, setPageId] = useState<string | null>(
    PASSPORT_DETAILS_PAGE_ID.PAYMENT_SUCCESS
  );
  const [bookingId, setBookingId] = useState<string | null>("");
  const [showModal, setShowModal] = useState(false);
  const [showBenefitsModal, setShowBenefitsModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [passportError, setPassportError] = useState<any>();
  const { type } = usePlanType();

  const {
    heading,
    subHeading,
    planCardTitle,
    SelectedPlanData,
    travellerData,
    addPassportTitle,
    addPassportInfo,
    passport,
    submitCtaText,
    skipCtaText,
    addPassportModalData,
    planBenefits,
  } = passportDetailsData;

  const {
    viewBenefitsCta,
    totalBenefits,
    featureGroup,
    claimSettled,
    tnc,
    planBenefitsInfo,
  } = planBenefits || {};

  const planBenefitsData = {
    productId: "",
    quoteId: "",
    packName: SelectedPlanData?.planType,
    insuranceType: SelectedPlanData?.planName,
    insPlanName: SelectedPlanData?.vendorName,
    vendorDisplayPrefix: SelectedPlanData?.vendorNamePrefix,
    planExtraInfo: claimSettled,
    service: planBenefitsInfo,
    ctaLink: tnc?.title,
    tncLink: tnc?.link,
    moreBenefits: featureGroup,
  };

  const experiment = useContext(ExperimentTrackingString) || "";

  useEffect(() => {
    analytics?.trackPageLoadEvent({
      pageName: "ADD_PASSPORT",
      bookingId: bookingId || "",
      experiment,
    });
    trackEvent({
      event_detail: {
        event_type: "action",
        event_name: "component_cta-clicked",
        event_value: "ADD_PASSPORT",
      },
    });
  }, []);

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);

    const pageid = queryParams.get("pageId");
    setPageId(pageid);

    const bookingid = queryParams.get("bookingId");
    setBookingId(bookingid);
  }, [pageId]);

  const handleAddPassportModal = () => {
    setShowModal(!showModal);
  };

  const navigateToNextPage = () => {
    if (pageId === PASSPORT_DETAILS_PAGE_ID.PAYMENT_SUCCESS) {
      navigationActions.navigateToOrderConfirmed({
        bookingId,
        type: type || "",
      });
    } else {
      navigationActions.navigateToOrderDetails({ bookingId });
    }
  };

  const handleSubmitClick = async (reqData?: ITravellerDetail[]) => {
    try {
      const partner = getPartnerOnClientSide();
      setIsLoading(true);
      const response = await editInsuranceDetails({
        partner,
        bookingID: bookingId,
        data: reqData,
      });
      setIsLoading(false);
      const { responseStatus } = response;
      if (responseStatus === RESPONSE_STATUS.SUCCESS) {
        navigateToNextPage();
      } else {
        setPassportError("Something went wrong!");
      }
    } catch (err: any) {
      setIsLoading(false);
      const { error } = err?.response?.data || {};
      setPassportError(error);
    }
  };

  if (passportError && typeof window !== "undefined") {
    throw new ClientError(passportError);
  }

  return (
    <React.Fragment>
      <DesktopLayout
        title={heading}
        subTitle={subHeading}
        leftComp={
          <React.Fragment>
            <InsuranceBooked
              title={planCardTitle}
              selectedPlanData={SelectedPlanData}
              viewBenefitsCta={viewBenefitsCta}
              handleViewBenefits={() => setShowBenefitsModal(true)}
            />
          </React.Fragment>
        }
        rightComp={
          <React.Fragment>
            <PassportNumComp
              isLoading={isLoading}
              title={addPassportTitle}
              info={addPassportInfo}
              submitCtaText={submitCtaText}
              skipCtaText={skipCtaText}
              travellerData={travellerData}
              passportFormData={passport}
              handleAddPassportModal={handleAddPassportModal}
              handleSubmitClick={handleSubmitClick}
            />
          </React.Fragment>
        }
      ></DesktopLayout>

      {showBenefitsModal && (
        <MoreBenefits
          handleClose={() => setShowBenefitsModal(false)}
          moreBenfitsTitle={totalBenefits}
          data={planBenefitsData}
        />
      )}

      {showModal && (
        <AddPassportModal
          data={addPassportModalData}
          handleSkip={navigateToNextPage}
          handleClose={handleAddPassportModal}
        />
      )}
    </React.Fragment>
  );
};

export default InsuranceAddPassport;
